import { CSS, styled } from "@givenwell/stitches";
import { colors, easing } from "@givenwell/theme";
import { ComponentPropsWithRef, ForwardedRef, ReactNode, forwardRef, useContext, useState } from "react";
import { FieldContext } from "./Field";

export type TextFieldProps = Omit<ComponentPropsWithRef<"input">, "size"> & {
  css?: CSS;
  invalid?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  label?: string;
  onValueChange?: (value: string) => void;
};

export const TextField = forwardRef(function TextField(
  { leftIcon, rightIcon, invalid, onValueChange, css, label, ...props }: TextFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const { inputProps } = useContext(FieldContext);
  const [isFocused, setIsFocused] = useState(false);
  const isNotEmpty = !!props.value;
  const isFloating = isFocused || isNotEmpty;
  return (
    <Root css={css}>
      <FloatingLabel floating={isFloating}>{label}</FloatingLabel>
      <StyledInput
        {...inputProps}
        hasLeftIcon={!!leftIcon}
        hasRightIcon={!!rightIcon}
        // invalid={invalid}
        {...props}
        ref={ref}
        onFocus={e => {
          props.onFocus?.(e);
          setIsFocused(true);
        }}
        onBlur={e => {
          props.onBlur?.(e);
          setIsFocused(false);
        }}
        onChange={e => {
          props.onChange?.(e);
          onValueChange?.(e.target.value);
        }}
      />
      {leftIcon && <LeftIcon>{leftIcon}</LeftIcon>}
      {rightIcon && <RightIcon>{rightIcon}</RightIcon>}
    </Root>
  );
});

const Root = styled("div", {
  pos: "relative",
  w: "100%",
  height: 56,
});
const StyledInput = styled("input", {
  minW: 0,
  w: "100%",
  height: "100%",
  px: 16,
  pt: 16,
  "&::placeholder": {
    color: colors.text3,
  },
  textAlign: "inherit",

  caretColor: colors.text,
  backgroundColor: colors.warmGreyHalf,
  borderRadius: 9,
  "&:focus": {
    outline: "none",
    borderColor: colors.blue500,
    boxShadow: `0 0 0 1px ${colors.blue500}`,
  },

  variants: {
    hasLeftIcon: {
      true: {
        pl: "var(--height)",
      },
    },
    hasRightIcon: {
      true: {
        pr: "var(--height)",
      },
    },
  },
});
const LeftIcon = styled("div", {
  pos: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  width: "var(--height)",
  color: colors.text3,
});
const RightIcon = styled("div", {
  pos: "absolute",
  right: 0,
  top: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  width: "var(--height)",
  color: colors.text3,
});

export const FloatingLabel = styled("label", {
  position: "absolute",
  top: 8,
  lineHeight: 1,
  left: 16,
  fontScale: "md",
  pointerEvents: "none",
  color: colors.text2,

  transition: `all 200ms ${easing.standard}`,
  transformOrigin: "top left",
  willChange: "transform",

  variants: {
    floating: {
      false: {
        top: "50%",
        transform: "translateY(-50%)",
        fontScale: "md",
        lineHeight: "16px",
      },
      true: {
        top: 8,
        transform: "translateY(0%)",
        fontScale: "xs",
        lineHeight: "16px",
      },
    },
  },
});
